/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import './AParameterCalculator.scss';
import { BodyText, Grid, Heading, NavTabs, Paper } from '@vaisala-rockhopper/components';
import { useTranslation } from 'react-i18next';
import { UnitPreset } from '../../../../modules/units/unitTypes';
import { isMobileOnly } from 'react-device-detect';
import CalibrationTableCard from '../cards/CalibrationTableCard';
import PolynomialGraph from '../calculators/PolynomialGraph';
import ResidualGraph from '../calculators/ResidualGraph';
import CalibrationTableCardMobile from '../cards/CalibrationTableCardMobile';

export interface CalculationResultData {
  'CCD': number[],
  'nD': number[],
  'nD_calc': number[],
  'nD_calcLine': number[],
  'A': number[],
  'CCD_calc': number[]
}

interface AParameterValue {
  "label": string,
  "value": string
}

interface AParameterCalculatorProps {
    theme: string
    currentUnitPreset: UnitPreset | undefined
}

const AParameterCalculator = ({ theme, currentUnitPreset }: AParameterCalculatorProps) => {
  const { t } = useTranslation('a-parameter-calculator');

  const [selectedTabKey, setSelectedTabKey] = React.useState('1');
  const [graphData, setGraphData] = React.useState<CalculationResultData | null>(null)
  const [graphLabels, setGraphLabels] = React.useState<number[]>([])

  const [aParameters, setAParameters] = React.useState<Array<AParameterValue>>([
    { "label": "a0", "value": "-"},
    { "label": "a1", "value": "-"},
    { "label": "a2", "value": "-"},
    { "label": "a3", "value": "-"}
  ])

  const onAParametersChange = (aparams: number[] | null) => {
    let aParametersCopy = [...aParameters]
    if (aparams !== null) {
      setAParameters(
        [
          {...aParametersCopy[0], "value": aparams[3].toString()},
          {...aParametersCopy[1], "value": aparams[2].toString()},
          {...aParametersCopy[2], "value": aparams[1].toString()},
          {...aParametersCopy[3], "value": aparams[0].toString()}
        ]
      )
    } else {
      setAParameters(
        [
          {...aParametersCopy[0], "value": "-"},
          {...aParametersCopy[1], "value": "-"},
          {...aParametersCopy[2], "value": "-"},
          {...aParametersCopy[3], "value": "-"}
        ]
      )
    }
  }

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [canvasContainerHeight, setCanvasContainerHeight] = React.useState(100)

  React.useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const height = containerRef.current.clientHeight;
        if (height) {
          setCanvasContainerHeight(height);
        }
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [containerRef]);

  const [diffValues, setDiffValues] = React.useState<number[]>([])

  return (
      <div className={`a-parameter-calculator-container`}>
          {isMobileOnly &&
            <CalibrationTableCardMobile
              onAParametersChange={onAParametersChange}
              setGraphData={setGraphData}
              setGraphLabels={setGraphLabels}
              setDiffValues={setDiffValues}
            />
          }
          {!isMobileOnly &&
            <CalibrationTableCard
              onAParametersChange={onAParametersChange}
              setGraphData={setGraphData}
              setGraphLabels={setGraphLabels}
              setDiffValues={setDiffValues}
            />
          }
          <Paper className="graph-card" ref={containerRef}>
            <NavTabs
              htmlId={['graph selection']}
              className={'graph-selection-tabs'}
              selectedTabKey={selectedTabKey}
              setSelectedTabKey={setSelectedTabKey}
              onSelectedTabChanged={selectedTabKey => setSelectedTabKey(selectedTabKey)}
            >
              <NavTabs.Tab tabKey="1" title={t('card.polynomial_graph')}>
                {/* {graphData !== null && */}
                  <PolynomialGraph
                    graphData={graphData}
                    graphLabels={graphLabels}
                    theme={theme}
                    canvasContainerHeight={canvasContainerHeight}
                    diffValues={diffValues}
                  />
                {/* } */}
              </NavTabs.Tab>
              <NavTabs.Tab tabKey="2" title={t('card.residual_graph')}>
                <ResidualGraph
                  theme={theme}
                  canvasContainerHeight={canvasContainerHeight}
                  graphLabels={graphLabels}
                  graphData={diffValues}
                  diffValues={diffValues}
                />
              </NavTabs.Tab>
            </NavTabs>
          </Paper>
          <Paper className="a-parameters-card">
            <Heading level={3}>
                  {t('card.a_parameters')}
            </Heading>
            <Grid>
              <Grid.Row>
                {aParameters.map((parameter, i)=> (
                  <Grid.Col
                    key={i}
                    xs={3}
                    sm={3}
                    md={3}
                    lg={3}
                    xl={3}
                  >
                    <BodyText><span className="small">{parameter.label}</span><br />{parameter.value ?? "-"}</BodyText>
                  </Grid.Col>
                ))}
              </Grid.Row>
            </Grid>
          </Paper>
          {/* <CalculationCard
              curveInformation={curveInformation}
              theme={theme}
              currentUnitPreset={currentUnitPreset}
          />
          <CurveParameterMatrix
              curveInformation={curveInformation}
          /> */}
      </div>
    );
};
export default AParameterCalculator;
